import type { PropsWithChildren, ReactElement } from 'react';
import { useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { getColorWithAlpha } from '@elfsight/lib-utils';
import * as RadixPopover from '@radix-ui/react-popover';
import { Slot } from '@radix-ui/react-slot';
import { useFiltersI18n } from '../../filters-i18n-provider';
import { useFiltersTheme } from '../../filters-theme-provider';
import { useFilters } from '../../widget-filters-provider';
import { ApplyFiltersButton, ClearFiltersButton } from '../buttons';
import { verticalScrollBar } from '../scrollbar-mixin';

interface FilterDropdownProps extends PropsWithChildren {
  maxWidth?: number;
  maxHeight?: number;
  trigger: ReactElement;
  onApplyRequest?: () => void;
  onClearRequest: () => void;
  displayClearButton?: boolean;
}

export const FilterDropdown = ({
  children,
  trigger,
  maxWidth = 300,
  maxHeight,
  onClearRequest,
  onApplyRequest,
  displayClearButton
}: FilterDropdownProps) => {
  const { applySelectedFilters, discardSelectedFilters } = useFilters();
  const { textColor, modalBackground } = useFiltersTheme();
  const { t } = useFiltersI18n();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  return (
    <Wrapper ref={anchorRef}>
      <RadixPopover.Root open={open}>
        <RadixPopover.Trigger onClick={() => setOpen((prev) => !prev)} asChild>
          {trigger}
        </RadixPopover.Trigger>
        <RadixPopover.Portal container={anchorRef.current}>
          <Content
            side="bottom"
            align="start"
            style={{ zIndex: 1000000 }}
            onPointerDownOutside={(event) => {
              event.preventDefault();
            }}
            onFocusOutside={(event) => {
              event.preventDefault();
            }}
            onInteractOutside={(event) => {
              const triggerEl = anchorRef.current?.firstElementChild;
              if (triggerEl && triggerEl.contains(event.target as Node)) {
                event.preventDefault();
                return;
              }
              discardSelectedFilters();
              setOpen(false);
            }}
            onEscapeKeyDown={() => {
              discardSelectedFilters();
              setOpen(false);
            }}
            collisionPadding={24}
            sideOffset={8}
            $maxWidth={maxWidth}
            $maxHeight={maxHeight}
            $backgroundColor={modalBackground}
            $textColor={textColor}
          >
            <ContentBody>{children}</ContentBody>

            <ContentFooter>
              {displayClearButton && (
                <Slot>
                  <ClearFiltersButton
                    onClick={() => {
                      onClearRequest();
                    }}
                  >
                    {t('Clear')}
                  </ClearFiltersButton>
                </Slot>
              )}

              <Slot
                onClick={() => {
                  setOpen(false);
                  applySelectedFilters();
                  onApplyRequest?.();
                }}
              >
                <ApplyFiltersButton>{t('Apply')}</ApplyFiltersButton>
              </Slot>
            </ContentFooter>
          </Content>
        </RadixPopover.Portal>
      </RadixPopover.Root>
    </Wrapper>
  );
};

const Wrapper = styled.div``;

const slideUpAndFade = keyframes`
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideDownAndFade = keyframes`
  from {
    opacity: 0;
    transform: translateY(-6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const ContentBody = styled.div`
  overflow-y: auto;
  overscroll-behavior: none;
  margin-right: 4px;
  padding: 16px;
`;

const ContentFooter = styled.div`
  display: flex;
  padding: 12px 16px;
  align-items: center;
  gap: 16px;
  justify-content: space-between;

  & > *:only-child {
    margin-left: auto;
  }
`;

const Content = styled(RadixPopover.Content)<{
  $maxWidth?: number;
  $maxHeight?: number;
  $backgroundColor: string;
  $textColor: string;
}>`
  background: ${({ $backgroundColor }) => $backgroundColor};
  display: flex;
  flex-direction: column;
  border-radius: 20px;
  border: 1px solid rgba(17, 17, 17, 0.1);
  box-shadow: 0 4px 12px 0 rgba(0, 0, 0, 0.2);
  overflow: hidden;
  width: 100%;
  min-width: 300px;
  animation-duration: 400ms;
  animation-timing-function: cubic-bezier(0.16, 1, 0.3, 1);
  will-change: transform, opacity;

  ${({ $maxWidth }) =>
    $maxWidth &&
    css`
      max-width: ${$maxWidth}px;
    `}
  ${({ $maxHeight }) =>
    $maxHeight &&
    css`
      max-height: ${$maxHeight}px;
    `}

  &[data-state='open'][data-side='top'] {
    animation-name: ${slideDownAndFade};
  }
  &[data-state='open'][data-side='bottom'] {
    animation-name: ${slideUpAndFade};
  }

  ${ContentBody} {
    ${({ $textColor }) => verticalScrollBar(getColorWithAlpha($textColor, 0.2))}
  }

  ${ContentFooter} {
    border-top: 1px solid
      ${({ $textColor }) => getColorWithAlpha($textColor, 0.1)};
  }
`;
